import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import API from "../../../services/api";
import "./staff.css";

const ROLES = [
 // 🎓 Academic Staff
  "Principal",
  "Vice Principal",
  "Head Master",
  "Head Mistress",
  "Class Teacher",
  "Subject Teacher",
  "Assistant Teacher",
  "PGT Teacher",
  "TGT Teacher",
  "PRT Teacher",
  "Nursery Teacher",
  "Special Educator",
  "Physical Education Teacher",
  "Music Teacher",
  "Dance Teacher",
  "Art Teacher",
  "Computer Teacher",
  "Lab Assistant",
  "Librarian",

  // 🏢 Administrative Staff
  "Accountant",
  "Clerk",
  "Office Staff",
  "Receptionist",
  "Admission Counselor",
  "Exam Coordinator",

  // 🚌 Transport Staff
  "Transport Manager",
  "Bus Driver",
  "Bus Conductor",

  // 🏠 Hostel Staff
  "Hostel Warden",
  "Assistant Warden",
  "Mess Manager",
  "Cook",
  "Kitchen Staff",

  // 🧹 Support Staff
  "Peon",
  "Cleaner",
  "Security Guard",
  "Maintenance Staff",
  "Electrician",
  "Plumber",

  // 🔧 Others
  "IT Support",
  "System Administrator",
  "Other",
];

export default function EditStaff() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    role: "",
    salary: "",
    joining_date: "",
  });

  const [submitting, setSubmitting] = useState(false);

  const getPhotoUrl = (p) => {
    if (!p) return null;
    if (p.startsWith("http")) return p;
    return `https://navith.cloud${p}`;
  };

  // Load initial data: from state OR from API
  const loadStaff = async () => {
    try {
      // If navigated from list with state
      if (location.state?.staff) {
        const s = location.state.staff;
        setForm({
          name: s.name || "",
          phone: s.phone || "",
          email: s.email || "",
          role: s.role || "",
          salary: s.salary || "",
          joining_date: s.joining_date || "",
        });
        if (s.photo) setPreview(getPhotoUrl(s.photo));
        setLoading(false);
        return;
      }

      // Fallback: fetch full list and pick one
      const res = await API.get("/school-admin/staff/", {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      const staffList = res.data.staff || [];
      const s = staffList.find((item) => String(item.id) === String(id));
      if (!s) {
        alert("Staff not found");
        navigate("/school-admin/staff");
        return;
      }

      setForm({
        name: s.name || "",
        phone: s.phone || "",
        email: s.email || "",
        role: s.role || "",
        salary: s.salary || "",
        joining_date: s.joining_date || "",
      });
      if (s.photo) setPreview(getPhotoUrl(s.photo));
    } catch (err) {
      console.error(err);
      alert("Error loading staff details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFile = (file) => {
    if (!file) return;
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return alert("Enter staff name");
    if (!form.role.trim()) return alert("Select role");

    try {
      setSubmitting(true);

      const fd = new FormData();
      Object.keys(form).forEach((key) => fd.append(key, form[key]));
      if (photo) fd.append("photo", photo);

     await API.post(`/school-admin/staff/update/${id}/`, fd, {
  headers: { "X-SCHOOL-ID": school.school.id },
});

      alert("Staff updated successfully!");
      navigate("/school-admin/staff");
    } catch (err) {
      console.error(err);
      alert("Error updating staff");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="staff-page">
        <p className="no-data">Loading staff details...</p>
      </div>
    );
  }

  return (
    <div className="staff-page">
      <div className="staff-header-row">
        <div>
          <h2 className="staff-title">Edit Staff Member</h2>
          <p className="staff-subtitle">
            Update staff details, salary or role information.
          </p>
        </div>
      </div>

      <div className="staff-form-wrapper">
        <form onSubmit={handleSubmit} className="staff-form">
          {/* PHOTO UPLOAD */}
          <div
            className="photo-upload-box staff-upload-box"
            onDrop={onDrop}
            onDragOver={onDragOver}
          >
            {preview ? (
              <img src={preview} className="preview-image" alt="Staff" />
            ) : (
              <div className="upload-inner">
                <p className="upload-title">Upload Staff Photo</p>
                <p className="upload-hint">
                  Drag & drop or click to browse
                </p>
                <label className="upload-btn">
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files[0])}
                  />
                </label>
              </div>
            )}
          </div>

          {/* NAME & ROLE */}
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                required
              >
                <option value="">Select role</option>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* CONTACT */}
          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* SALARY & JOINING DATE */}
          <div className="form-row">
            <div className="form-group">
              <label>Monthly Salary (₹)</label>
              <input
                name="salary"
                value={form.salary}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Joining Date</label>
              <input
                type="date"
                name="joining_date"
                value={form.joining_date || ""}
                onChange={handleChange}
              />
            </div>
          </div>

          <button className="btn-submit" disabled={submitting}>
            {submitting ? "Saving..." : "Update Staff"}
          </button>
        </form>
      </div>
    </div>
  );
}